import {
    faArrowDownWideShort,
    faArrowUpShortWide,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Input,
    Select,
    SelectItem,
} from "@heroui/react";
import { Key } from "@react-types/shared";
import { useEffect, useState } from "react";

export type MobileNetworkFilter = {
    id: string;
    label: string;
    placeholder?: string;
    value?: string;
    defaultValue?: string;
    inputType: "select" | "autocomplete";
    allLabel?: string;
    options: { key: string; label: string }[];
};

type MobileNetworkFilterDrawerProps = {
    isOpen: boolean;
    onClose: () => void;

    searchValue?: string;
    filters: MobileNetworkFilter[];

    ordering?: string;
    sortOptions: { key: string; label: string }[];

    onApply: (filters: {
        searchValue: string;
        filters: Record<string, string | undefined>;
        sortKey: string;
        sortDesc: boolean;
    }) => void;

    onClear: () => void;
};

function getInitialFilterValues(filters: MobileNetworkFilter[]) {
    return filters.reduce<Record<string, Key | null>>((acc, filter) => {
        if (filter.inputType === "select") {
            acc[filter.id] = filter.value ?? filter.defaultValue ?? "all";
        } else {
            acc[filter.id] =
                filter.value && filter.value !== "all" ? filter.value : null;
        }

        return acc;
    }, {});
}

function getClearedFilterValues(filters: MobileNetworkFilter[]) {
    return filters.reduce<Record<string, Key | null>>((acc, filter) => {
        acc[filter.id] =
            filter.inputType === "select"
                ? (filter.defaultValue ?? "all")
                : null;

        return acc;
    }, {});
}

function getInitialSortKey(
    ordering: string | undefined,
    sortOptions: { key: string; label: string }[],
) {
    return ordering
        ? ordering.replace(/^-/, "")
        : (sortOptions[0]?.key ?? null);
}

export default function MobileNetworkFilterDrawer({
    isOpen,
    onClose,
    searchValue,
    filters,
    ordering,
    sortOptions,
    onApply,
    onClear,
}: MobileNetworkFilterDrawerProps) {
    const [localSearch, setLocalSearch] = useState(searchValue ?? "");
    const [localFilterValues, setLocalFilterValues] = useState<
        Record<string, Key | null>
    >(() => getInitialFilterValues(filters));

    const [localSortKey, setLocalSortKey] = useState<Key | null>(() =>
        getInitialSortKey(ordering, sortOptions),
    );

    const [localSortDesc, setLocalSortDesc] = useState(
        !!ordering && ordering.startsWith("-"),
    );

    useEffect(() => {
        if (!isOpen) return;

        setLocalSearch(searchValue ?? "");
        setLocalFilterValues(getInitialFilterValues(filters));
        setLocalSortKey(getInitialSortKey(ordering, sortOptions));
        setLocalSortDesc(!!ordering && ordering.startsWith("-"));
    }, [filters, isOpen, ordering, searchValue, sortOptions]);

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            placement="bottom"
            size="md"
            scrollBehavior="inside"
        >
            <DrawerContent>
                <DrawerHeader>Filters</DrawerHeader>

                <DrawerBody className="flex flex-col gap-3">
                    <Input
                        label="Search"
                        placeholder="Search..."
                        size="sm"
                        value={localSearch}
                        onValueChange={setLocalSearch}
                        isClearable
                        onClear={() => setLocalSearch("")}
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                    />

                    {filters.map((filter) =>
                        filter.inputType === "autocomplete" ? (
                            <Autocomplete
                                key={filter.id}
                                label={filter.label}
                                placeholder={filter.placeholder ?? filter.label}
                                size="sm"
                                selectedKey={localFilterValues[filter.id]}
                                onSelectionChange={(key) => {
                                    setLocalFilterValues((prev) => ({
                                        ...prev,
                                        [filter.id]: key,
                                    }));
                                }}
                                allowsCustomValue={false}
                                isVirtualized
                            >
                                {filter.options.map((option) => (
                                    <AutocompleteItem
                                        key={option.key}
                                        textValue={option.label}
                                    >
                                        {option.label}
                                    </AutocompleteItem>
                                ))}
                            </Autocomplete>
                        ) : (
                            <Select
                                key={filter.id}
                                label={filter.label}
                                size="sm"
                                selectedKeys={
                                    localFilterValues[filter.id] != null
                                        ? [
                                              localFilterValues[
                                                  filter.id
                                              ]!.toString(),
                                          ]
                                        : []
                                }
                                onSelectionChange={(keys) => {
                                    const first = Array.from(
                                        keys as Set<Key>,
                                    )[0];

                                    setLocalFilterValues((prev) => ({
                                        ...prev,
                                        [filter.id]: first ?? null,
                                    }));
                                }}
                            >
                                {[
                                    {
                                        key: "all",
                                        label:
                                            filter.allLabel ??
                                            `All ${filter.label}`,
                                    },
                                    ...filter.options,
                                ].map((option) => (
                                    <SelectItem
                                        key={option.key}
                                        textValue={option.label}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </Select>
                        ),
                    )}

                    {sortOptions.length > 0 && (
                        <div className="flex flex-row gap-2 items-end">
                            <Select
                                label="Sort by"
                                size="sm"
                                className="flex-1"
                                selectedKeys={
                                    localSortKey != null
                                        ? [localSortKey.toString()]
                                        : []
                                }
                                onSelectionChange={(keys) => {
                                    const first = Array.from(
                                        keys as Set<Key>,
                                    )[0];

                                    setLocalSortKey(first ?? null);
                                }}
                            >
                                {sortOptions.map((option) => (
                                    <SelectItem key={option.key}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </Select>

                            <Button
                                size="sm"
                                isIconOnly
                                variant="flat"
                                aria-label={
                                    localSortDesc ? "Descending" : "Ascending"
                                }
                                onPress={() =>
                                    setLocalSortDesc((prev) => !prev)
                                }
                            >
                                <FontAwesomeIcon
                                    icon={
                                        localSortDesc
                                            ? faArrowDownWideShort
                                            : faArrowUpShortWide
                                    }
                                />
                            </Button>
                        </div>
                    )}
                </DrawerBody>

                <DrawerFooter>
                    <Button
                        variant="faded"
                        onPress={() => {
                            setLocalSearch("");
                            setLocalFilterValues(
                                getClearedFilterValues(filters),
                            );
                            onClear();
                            onClose();
                        }}
                    >
                        Clear All
                    </Button>

                    <Button
                        color="primary"
                        onPress={() => {
                            const appliedFilters = filters.reduce<
                                Record<string, string | undefined>
                            >((acc, filter) => {
                                const value = localFilterValues[filter.id];

                                acc[filter.id] =
                                    value == null
                                        ? undefined
                                        : value.toString();

                                return acc;
                            }, {});

                            onApply({
                                searchValue: localSearch.trim(),
                                filters: appliedFilters,
                                sortKey: localSortKey
                                    ? localSortKey.toString()
                                    : "",
                                sortDesc: localSortDesc,
                            });

                            onClose();
                        }}
                    >
                        Apply
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
